import React, { Component } from "react";
import FD from "../../../views/RestAPI/fetchData";
import App_data from "../../../views/config/App_data";

/* eslint-disable*/

/**
 * PayPal Smart Button (Orders v2) — React 15 compatible.
 *
 * Loads PayPal's JS SDK via a <script> tag (the same pattern Checkout.js uses
 * for Stripe.js v3) and renders paypal.Buttons() imperatively into a div
 * ref. We avoid @paypal/react-paypal-js because it requires React ≥ 16.8.
 *
 * Flow:
 *   1. Mount → load https://www.paypal.com/sdk/js?client-id=...&currency=USD
 *   2. SDK ready → call paypal.Buttons({ createOrder, onApprove, ... }).render()
 *   3. User clicks → createOrder hits our /paypalCreateOrder, returns paypalOrderId
 *   4. PayPal popup → user approves
 *   5. onApprove → hits our /paypalCaptureOrder, parent routes to Thank You
 *
 * Props:
 *   paypalClientId  (string)   PayPal Client ID from /getPaymentCred
 *   paypalEnv       (string)   'sandbox' | 'live' — informational; the SDK
 *                              picks sandbox/live by Client ID automatically.
 *   getCart         (function) Returns { userCart, amount, idname } — called
 *                              at click-time so values reflect current state.
 *   onSuccess       (function) Called with { order_id } after capture.
 *   onError         (function) Called with a human-readable message.
 *   disabled        (boolean)  Skip rendering (e.g. amount = 0).
 */
class PayPalButton extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sdkReady: false,
      sdkError: false,
      rendering: false,
    };
    this.containerRef = null;
    this.setContainerRef = (el) => { this.containerRef = el; };
    this.handleScriptLoaded = this.handleScriptLoaded.bind(this);
    this.handleScriptError = this.handleScriptError.bind(this);
  }

  componentDidMount() {
    this.loadSdk();
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevState.sdkReady && this.state.sdkReady && !this.state.rendering) {
      this.renderButtons();
    }
  }

  componentWillUnmount() {
    if (this._sdkScript && this._sdkScript.parentNode) {
      this._sdkScript.removeEventListener("load", this.handleScriptLoaded);
      this._sdkScript.removeEventListener("error", this.handleScriptError);
    }
    if (this.containerRef) {
      // The PayPal SDK doesn't expose a teardown — clearing the host div is
      // the documented escape hatch. Prevents orphan iframes / event handlers
      // when the user re-mounts (e.g. toggling payment method).
      this.containerRef.innerHTML = "";
    }
  }

  loadSdk() {
    if (!this.props.paypalClientId) {
      this.setState({ sdkError: true });
      return;
    }
    if (window.paypal && window.paypal.Buttons) {
      this.setState({ sdkReady: true });
      return;
    }
    const existing = document.querySelector('script[data-paypal-sdk="1"]');
    if (existing) {
      // Another instance started the load — piggy-back.
      existing.addEventListener("load", this.handleScriptLoaded);
      existing.addEventListener("error", this.handleScriptError);
      this._sdkScript = existing;
      return;
    }
    const script = document.createElement("script");
    const params = [
      "client-id=" + encodeURIComponent(this.props.paypalClientId),
      "currency=USD",
      "intent=capture",
      "components=buttons",
      // Pay Later messaging will surface automatically for eligible buyers.
      "enable-funding=paylater",
      // Suppress the secondary "Debit or Credit Card" PayPal button — that
      // funding source duplicates our own Stripe card form above.
      "disable-funding=card",
    ].join("&");
    script.src = "https://www.paypal.com/sdk/js?" + params;
    script.async = true;
    script.dataset.paypalSdk = "1";
    script.addEventListener("load", this.handleScriptLoaded);
    script.addEventListener("error", this.handleScriptError);
    document.body.appendChild(script);
    this._sdkScript = script;
  }

  handleScriptLoaded() {
    if (window.paypal && window.paypal.Buttons) {
      this.setState({ sdkReady: true });
    } else {
      this.setState({ sdkError: true });
    }
  }

  handleScriptError() {
    this.setState({ sdkError: true });
  }

  renderButtons() {
    if (!window.paypal || !window.paypal.Buttons || !this.containerRef) return;
    this.setState({ rendering: true });

    const buttons = window.paypal.Buttons({
      style: {
        layout: "vertical",
        color: "gold",
        shape: "rect",
        label: "paypal",
      },

      createOrder: async () => {
        try {
          const ctx = (typeof this.props.getCart === "function")
            ? (this.props.getCart() || {})
            : {};
          const payload = {
            userCart: ctx.userCart || [],
            amount: ctx.amount || 0,
            idname: ctx.idname || "",
          };
          const resp = await FD.callAPI(
            App_data.backend_api + "/checkout_v1/paypalCreateOrder",
            payload,
          );
          if (resp && resp.success && resp.paypalOrderId) {
            return resp.paypalOrderId;
          }
          const msg = (resp && resp.message) || "Could not start PayPal checkout.";
          if (this.props.onError) this.props.onError(msg);
          throw new Error(msg);
        } catch (err) {
          if (this.props.onError) {
            this.props.onError((err && err.message) || "PayPal initialisation failed.");
          }
          throw err;
        }
      },

      onApprove: async (data) => {
        try {
          const ctx = (typeof this.props.getCart === "function")
            ? (this.props.getCart() || {})
            : {};
          const payload = {
            paypalOrderId: data.orderID,
            userCart: ctx.userCart || [],
            amount: ctx.amount || 0,
            idname: ctx.idname || "",
          };
          const resp = await FD.callAPI(
            App_data.backend_api + "/checkout_v1/paypalCaptureOrder",
            payload,
          );
          if (resp && resp.success) {
            const orderId = resp.order_id || (resp.orders && resp.orders.id);
            if (this.props.onSuccess) this.props.onSuccess({ order_id: orderId });
          } else {
            const msg = (resp && resp.message) || "PayPal payment could not be finalised.";
            if (this.props.onError) this.props.onError(msg);
          }
        } catch (err) {
          if (this.props.onError) {
            this.props.onError((err && err.message) || "PayPal capture failed.");
          }
        }
      },

      onError: (err) => {
        // Fires for SDK-level errors before our createOrder/onApprove run.
        if (this.props.onError) {
          this.props.onError((err && err.message) || "PayPal error.");
        }
      },

      onCancel: () => {
        // User closed the PayPal popup. Not an error — just re-enable parent state.
        if (this.props.onCancel) this.props.onCancel();
      },
    });

    if (buttons.isEligible && !buttons.isEligible()) {
      this.setState({ sdkError: true });
      return;
    }
    buttons.render(this.containerRef).catch(() => {
      this.setState({ sdkError: true });
    });
  }

  render() {
    if (this.props.disabled) return null;

    if (this.state.sdkError) {
      return (
        <div className="chk_paypal_error">
          PayPal isn't available right now. Please refresh or use a credit card.
        </div>
      );
    }

    return (
      <div className="chk_paypal_wrapper">
        <div ref={this.setContainerRef} className="chk_paypal_buttons" />
        {!this.state.sdkReady && (
          <div className="chk_paypal_loading">Loading PayPal…</div>
        )}
      </div>
    );
  }
}

export default PayPalButton;



// WEBPACK FOOTER //
// ./src/components/CheckoutComponents/PaymentMethods/PayPalButton.js